import { useEffect, useMemo, useState } from 'react';
import { createJobTicket, getJobTicketFilename, getPreflightReportFilename, getProjectJsonFilename } from '../lib/createJobTicket';
import { getAssetStorageCapabilities } from '../lib/assetStoragePolicy';
import { mockCloudAssetAdapter } from '../lib/cloudAssetAdapter';
import { listLocalAssets } from '../lib/localAssetStore';
import { runPreflight } from '../lib/runPreflight';
import { usePhotoPreviewUrls } from '../lib/usePhotoPreviewUrls';
import { loadLastProject, saveProject } from '../lib/projectStorage';
import type { CalendarPage, CalendarPhoto, CalendarProject, PreflightCheckId, PreflightIssue, PreflightReport, PreflightSeverity } from '../types/calendarProject';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/ui/Logo';

const progressSteps = ['Produkt', 'Fotky', 'Návrh', 'Editor', 'Preflight'];
const productLabels = { basic_year_story: 'Basic Year Story', family_planner_cz: 'Family Planner CZ', fineart_gallery: 'FineArt Gallery' };
const statusLabels: Record<PreflightSeverity, string> = { ok: 'V pořádku', warning: 'Upozornění', error: 'Chyby' };
const checkLabels: Record<PreflightCheckId, string> = {
  page_count: 'Počet stran',
  missing_photos: 'Chybějící fotky',
  photo_quality: 'Kvalita fotek',
  screenshot_warning: 'Screenshoty',
  unusual_aspect_ratio: 'Poměr stran',
  crop_extreme: 'Extrémní ořez',
  text_length: 'Délka textů',
  safe_zone: 'Bezpečná zóna',
  bleed: 'Spadávka',
  local_assets: 'Lokální assety',
};

function flattenIssues(report: PreflightReport) {
  return report.checks.flatMap((check) => check.issues);
}

function downloadJson(fileName: string, payload: unknown) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function getReadinessMessage(report: PreflightReport) {
  if (report.errorCount > 0) return 'Projekt obsahuje chyby. Export metadat je možný, ale produkční export je blokovaný.';
  if (report.warningCount > 0) return 'Projekt obsahuje upozornění. Můžeš stáhnout metadata nebo pokračovat po ruční kontrole.';
  return 'Projekt je připravený pro exportní balíček.';
}

function getPhoto(project: CalendarProject, page?: CalendarPage) {
  return project.photos.find((photo) => photo.id === page?.photoId);
}

function getPage(project: CalendarProject, issue?: PreflightIssue) {
  return project.pages.find((page) => page.id === issue?.pageId);
}

function pageLabel(page?: CalendarPage) {
  if (!page) return 'Celý projekt';
  return page.type === 'cover' ? 'Titulka' : `${page.title} ${page.year ?? ''}`.trim();
}

function getCheckDescription(check?: PreflightReport['checks'][number]) {
  if (!check) return 'Vyber kontrolu vlevo pro detail.';
  if (check.id === 'local_assets' && check.severity === 'warning') {
    return 'Originály nejsou v tomto prohlížeči dostupné. Znovu nahraj fotky nebo použij budoucí cloud storage.';
  }
  return check.description;
}

function MiniPreview({ page, photo, previewUrl }: { page?: CalendarPage; photo?: CalendarPhoto; previewUrl?: string }) {
  if (!page) {
    return <div className="preflight-preview-placeholder">Vyber kontrolu nebo issue pro detail stránky.</div>;
  }

  return (
    <div className="preflight-page-mini">
      <div className="preflight-page-mini__photo">
        {photo && previewUrl ? <img src={previewUrl} alt={photo.fileName} /> : <span>Lokální fotka není dostupná</span>}
      </div>
      <div className="preflight-page-mini__meta">
        <strong>{pageLabel(page)}</strong>
        <span>{photo?.fileName ?? 'Bez fotky'}</span>
        <span>Score: {photo?.score ?? '—'}</span>
        {photo?.issues.length ? <small>{photo.issues.join(' · ')}</small> : <small>Bez uložených issues.</small>}
      </div>
    </div>
  );
}

export function PreflightFlow() {
  const [project, setProject] = useState<CalendarProject | null>(null);
  const [report, setReport] = useState<PreflightReport | null>(null);
  const [selectedCheckId, setSelectedCheckId] = useState<PreflightCheckId>('page_count');
  const [selectedIssueId, setSelectedIssueId] = useState<string>('');
  const [localAssetCount, setLocalAssetCount] = useState(0);
  const previewUrls = usePhotoPreviewUrls(project);
  const localStorageCapabilities = getAssetStorageCapabilities('indexeddb-local-mvp');
  const plannedCloudCapabilities = getAssetStorageCapabilities('cloud-r2-planned');
  const uploadPlan = project ? mockCloudAssetAdapter.createUploadPlan(project) : null;
  const missingCloudConfigNotes = mockCloudAssetAdapter.explainMissingConfiguration();

  const runAndSaveReport = async (sourceProject: CalendarProject) => {
    let availableAssetIds: Set<string> | undefined;
    try {
      const localAssets = await listLocalAssets(sourceProject.id);
      setLocalAssetCount(localAssets.length);
      availableAssetIds = new Set(localAssets.map((asset) => asset.photoId));
    } catch {
      setLocalAssetCount(0);
      availableAssetIds = undefined;
    }

    const nextReport = runPreflight(sourceProject, availableAssetIds);
    const updatedProject: CalendarProject = {
      ...sourceProject,
      updatedAt: new Date().toISOString(),
      preflight: {
        status: nextReport.overallStatus,
        issues: flattenIssues(nextReport),
        lastReport: nextReport,
      },
    };
    saveProject(updatedProject);
    setProject(updatedProject);
    setReport(nextReport);
    if (!nextReport.checks.some((check) => check.id === selectedCheckId)) {
      setSelectedCheckId(nextReport.checks[0]?.id ?? 'page_count');
    }
    setSelectedIssueId('');
  };

  useEffect(() => {
    const loadedProject = loadLastProject();
    setProject(loadedProject);
    if (loadedProject?.pages.length) {
      void runAndSaveReport(loadedProject);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCheck = report?.checks.find((check) => check.id === selectedCheckId) ?? report?.checks[0];
  const selectedIssue = useMemo(() => {
    if (!selectedCheck) return undefined;
    return selectedCheck.issues.find((issue) => issue.id === selectedIssueId) ?? selectedCheck.issues[0];
  }, [selectedCheck, selectedIssueId]);
  const selectedPage = project ? getPage(project, selectedIssue) : undefined;
  const selectedPhoto = project ? getPhoto(project, selectedPage) : undefined;

  const downloadProjectJson = () => {
    if (!project) return;
    downloadJson(getProjectJsonFilename(project.id), {
      ...project,
      photos: project.photos.map(({ objectUrl: _objectUrl, ...photo }) => photo),
    });
  };

  const downloadPreflightReport = () => {
    if (!project) return;
    downloadJson(getPreflightReportFilename(project.id), project.preflight.lastReport ?? report);
  };

  const downloadJobTicket = () => {
    if (!project) return;
    downloadJson(getJobTicketFilename(project.id), createJobTicket(project));
  };

  if (!project) {
    return (
      <main className="preflight-shell">
        <section className="preflight-empty-state">
          <Logo />
          <h1>Nejdřív vytvoř projekt</h1>
          <p>Preflight navazuje na lokální projekt vytvořený ve start flow.</p>
          <Button href="/start">Začít výběrem</Button>
        </section>
      </main>
    );
  }

  if (!project.pages.length) {
    return (
      <main className="preflight-shell">
        <section className="preflight-empty-state">
          <Logo />
          <h1>Nejdřív vytvoř návrh</h1>
          <p>Preflight potřebuje titulku a 12 měsíčních stránek z Magic Draftu.</p>
          <Button href="/draft">Zpět na návrh</Button>
        </section>
      </main>
    );
  }

  if (!report) return null;

  return (
    <main className="preflight-shell">
      <div className="preflight-card">
        <header className="preflight-header">
          <Logo />
          <div className="preflight-header__project">
            <strong>{productLabels[project.productId]}</strong>
            <span className={`preflight-status is-${report.overallStatus}`}>{statusLabels[report.overallStatus]}</span>
          </div>
          <div className="preflight-header__actions">
            <Button href="/projects" variant="secondary">Moje projekty</Button>
            <Button href="/editor" variant="secondary">Zpět do editoru</Button>
            <button type="button" onClick={() => void runAndSaveReport(project)}>Spustit kontrolu</button>
            <button type="button" disabled>Exportovat</button>
          </div>
        </header>

        <nav className="preflight-progress" aria-label="Kroky Magic Flow">
          {progressSteps.map((step, index) => <span key={step} className={index === 4 ? 'is-active' : ''}><small>0{index + 1}</small>{step}</span>)}
        </nav>

        <section className="preflight-hero">
          <p className="eyebrow">PREFLIGHT V0.1</p>
          <h1>Preflight kontrola</h1>
          <p>Zkontrolujeme fotky, stránky, texty a základní tisková rizika před exportem.</p>
        </section>

        <section className="preflight-summary" aria-label="Shrnutí preflight kontroly">
          <article><span>Počet stran</span><strong>{report.totalPages}</strong></article>
          <article><span>OK kontroly</span><strong>{report.okCount}</strong></article>
          <article><span>Upozornění</span><strong>{report.warningCount}</strong></article>
          <article><span>Chyby</span><strong>{report.errorCount}</strong></article>
          <article className={`is-${report.overallStatus}`}><span>Celkový stav</span><strong>{statusLabels[report.overallStatus]}</strong></article>
        </section>

        <section className="preflight-main">
          <div className="preflight-checks">
            {report.checks.map((check, index) => (
              <button key={check.id} type="button" className={check.id === selectedCheck?.id ? 'is-active' : ''} onClick={() => { setSelectedCheckId(check.id); setSelectedIssueId(''); }}>
                <span className="preflight-check-number">{String(index + 1).padStart(2, '0')}</span>
                <span><strong>{check.label}</strong><small>{check.description}</small></span>
                <span className={`preflight-status is-${check.severity}`}>{statusLabels[check.severity]}</span>
                <span className="preflight-counts">OK {check.passed} · W {check.warnings} · E {check.errors}</span>
              </button>
            ))}
          </div>

          <div className="preflight-detail">
            <div className="preflight-detail__header">
              <div>
                <p className="eyebrow">Kontrola</p>
                <h2>{selectedCheck ? checkLabels[selectedCheck.id] : 'Celkové shrnutí'}</h2>
                <p>{getCheckDescription(selectedCheck)}</p>
              </div>
              {selectedCheck && <span className={`preflight-status is-${selectedCheck.severity}`}>{statusLabels[selectedCheck.severity]}</span>}
            </div>

            <div className="preflight-detail__grid">
              <MiniPreview page={selectedPage} photo={selectedPhoto} previewUrl={selectedPhoto ? previewUrls[selectedPhoto.id] : undefined} />
              <div className="preflight-issues">
                {selectedCheck?.issues.length ? selectedCheck.issues.map((issueItem) => {
                  const issuePage = project.pages.find((page) => page.id === issueItem.pageId);
                  return (
                    <article key={issueItem.id} className={issueItem.id === selectedIssue?.id ? 'is-selected' : ''} onClick={() => setSelectedIssueId(issueItem.id)}>
                      <span className={`preflight-status is-${issueItem.severity}`}>{statusLabels[issueItem.severity]}</span>
                      <h3>{issueItem.title}</h3>
                      <p>{issueItem.message}</p>
                      <small>Stránka: {pageLabel(issuePage)}</small>
                      <strong>{issueItem.recommendation}</strong>
                      <Button href={issueItem.pageId ? `/editor?page=${issueItem.pageId}` : '/editor'} variant="secondary">Otevřít v editoru</Button>
                    </article>
                  );
                }) : (
                  <article className="preflight-no-issues">
                    <h3>Bez issues</h3>
                    <p>Tato kontrola nehlásí žádné problémy.</p>
                    <strong>Pokračuj další kontrolou nebo připrav výstupy.</strong>
                  </article>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="preflight-outputs">
          <div>
            <p className="eyebrow">Výstupy</p>
            <h2>Exportní balíček</h2>
            <p>Tato MVP verze připravuje metadata a tiskový HTML náhled. Skutečný PDF/X export a produkční asset package přijdou po napojení cloud asset storage.</p>
            <p className="preflight-export-note">JSON export neobsahuje fotky. Fotky jsou zatím pouze lokálně v tomto prohlížeči.</p>
            <div className={`preflight-readiness is-${report.overallStatus}`}>{getReadinessMessage(report)}</div>
            <div className="preflight-export-state" aria-label="MVP exportní stav">
              <strong>MVP exportní stav</strong>
              <dl>
                <div><dt>Aktivní</dt><dd>Project JSON, Preflight report JSON, Job ticket JSON, HTML print preview</dd></div>
                <div><dt>Skeleton</dt><dd>Production PDF/PDF-X</dd></div>
                <div><dt>Později</dt><dd>Assets package, Premium vrstvy</dd></div>
              </dl>
            </div>
            <div className="preflight-asset-storage" aria-label="Asset storage stav">
              <div>
                <p className="eyebrow">Asset storage stav</p>
                <h3>Browser-only IndexedDB MVP</h3>
                <p>Cloud R2 upload má připravený kontrakt, ale je zatím vypnutý.</p>
              </div>
              <dl>
                <div><dt>Aktuální režim</dt><dd>Browser-only IndexedDB MVP</dd></div>
                <div><dt>Originály fotek</dt><dd>Uložené lokálně v tomto prohlížeči ({localAssetCount}/{project.photos.length})</dd></div>
                <div><dt>Project JSON</dt><dd>Pouze metadata, bez base64 a bez blobů</dd></div>
                <div><dt>Produkční asset package</dt><dd>Zatím není aktivní</dd></div>
                <div><dt>Cloud R2 upload</dt><dd>Připravený kontrakt, zatím vypnutý</dd></div>
              </dl>
              <div className="preflight-asset-storage__notes">
                {[...localStorageCapabilities.notes, ...plannedCloudCapabilities.notes, ...(uploadPlan ? [`Plán počítá s ${uploadPlan.expectedAssetCount} assety a endpointy: ${uploadPlan.requiredEndpoints.join(' · ')}`] : []), ...missingCloudConfigNotes].map((note) => <span key={note}>{note}</span>)}
              </div>
              <button type="button" disabled title="Cloud upload bude aktivní až po přidání Worker API a R2 konfigurace.">Připravit cloud upload</button>
              <small>Cloud upload bude aktivní až po přidání Worker API a R2 konfigurace.</small>
            </div>
          </div>
          <div className="preflight-output-grid">
            <article className="is-preview"><strong>HTML print preview</strong><span>Aktivní tiskový HTML náhled</span><Button href="/print-preview" variant="secondary">Otevřít HTML print preview</Button></article>
            <article className="is-active"><strong>Objednávkový draft</strong><span>{report.errorCount > 0 ? 'Objednávkový draft lze připravit, produkční export je blokovaný.' : 'Browser-only objednávkový draft'}</span><Button href="/order" variant="secondary">Pokračovat k objednávce</Button></article>
            <article className="is-skeleton"><strong>Production PDF/PDF-X</strong><span>Skeleton exportní vrstva</span><button type="button" disabled>PDF/X později</button></article>
            <article className="is-active"><strong>Project JSON</strong><span>Aktivní metadata bez fotek</span><button type="button" onClick={downloadProjectJson}>Stáhnout project.json</button></article>
            <article className="is-active"><strong>Preflight report</strong><span>Aktuální kontrola JSON</span><button type="button" onClick={downloadPreflightReport}>Stáhnout preflight-report.json</button></article>
            <article className="is-active"><strong>Job ticket</strong><span>Produkční metadata JSON</span><button type="button" onClick={downloadJobTicket}>Stáhnout job-ticket.json</button></article>
            <article className="is-later"><strong>Assets package</strong><span>Později po cloud storage</span><button type="button" disabled>Později</button></article>
            <article className="is-later"><strong>Premium vrstvy</strong><span>Později</span><button type="button" disabled>Později</button></article>
          </div>
        </section>
      </div>
    </main>
  );
}
